import { ImageIcon, Images, Upload, Video, X } from "lucide-react"
import { type CSSProperties, type ReactNode, type Ref } from "react"
import type { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core"
import { Button } from "@/components/ui/primitives"
import { cn } from "@/lib/utils"
import type { FlowChainMedia, FlowNodeRow } from "@/types/flow"

export const FLOW_SLOT_FRAME =
  "relative flex h-14 w-full items-center overflow-visible rounded-lg border border-dashed border-ui-border-strong bg-ui-surface-sunken/40"

const SLOT_CARD =
  "relative z-10 flex h-full min-w-0 flex-1 items-center gap-2 rounded-sm bg-ui-surface-raised pr-1 pl-1"

export type FlowSlotTile = { url: string; video: boolean }

export function flowSlotSourceLabel(source: FlowNodeRow | undefined, fallback: string): string {
  const name = source?.title?.trim() || source?.entity_name?.trim() || source?.shot_label?.trim()
  return name || fallback
}

export function flowSlotViewUrl(source: FlowNodeRow, media: FlowChainMedia): string | null {
  if (media === "video") return source.media_url || source.download_url
  return source.download_url || source.media_url
}

export function flowSlotTile(source: FlowNodeRow, media: FlowChainMedia): FlowSlotTile | null {
  if (source.media_thumb_url) return { url: source.media_thumb_url, video: false }
  const url = source.media_url || source.download_url
  if (!url) return null
  return { url, video: media === "video" }
}

export function FlowSlotMediaLabel({
  media,
  label,
  className,
}: {
  media: FlowChainMedia
  label: string
  className?: string
}) {
  const Icon = media === "video" ? Video : ImageIcon
  return (
    <span className={cn("flex min-w-0 items-center gap-1 text-xs text-ui-text-muted", className)}>
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
    </span>
  )
}

export function FlowSlotHeading({
  label,
  media,
  mediaLabel,
}: {
  label: string
  media: FlowChainMedia
  mediaLabel: string
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="truncate text-sm text-ui-text-primary">{label}</span>
      <FlowSlotMediaLabel media={media} label={mediaLabel} className="shrink-0" />
    </div>
  )
}

export function FlowSlotFrame({
  className,
  highlight,
  frameRef,
  children,
}: {
  className?: string
  highlight?: boolean
  frameRef?: Ref<HTMLDivElement>
  children: ReactNode
}) {
  return (
    <div ref={frameRef} className={cn(FLOW_SLOT_FRAME, className, highlight && "border-ui-accent bg-ui-accent/10")}>
      {children}
    </div>
  )
}

export function FlowSlotEmptyHint({ children }: { children: ReactNode }) {
  return <p className="min-w-0 flex-1 truncate text-sm text-ui-text-muted">{children}</p>
}

export function FlowSlotAttachButtons({
  disabled,
  uploadLabel,
  libraryLabel,
  onUpload,
  onLibrary,
}: {
  disabled?: boolean
  uploadLabel: string
  libraryLabel: string
  onUpload: () => void
  onLibrary: () => void
}) {
  return (
    <div className="flex shrink-0 gap-2">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 shrink-0"
        disabled={disabled}
        aria-label={uploadLabel}
        title={uploadLabel}
        onClick={onUpload}
      >
        <Upload className="h-4 w-4" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 shrink-0"
        disabled={disabled}
        aria-label={libraryLabel}
        title={libraryLabel}
        onClick={onLibrary}
      >
        <Images className="h-4 w-4" aria-hidden />
      </Button>
    </div>
  )
}

export function FlowSlotCard({
  name,
  mediaLabel,
  tile,
  viewUrl,
  canDisconnect,
  disconnectLabel,
  disconnectDisabled,
  onOpen,
  onDisconnect,
  className,
  style,
  cardRef,
  dragAttributes,
  dragListeners,
}: {
  name: string
  mediaLabel: string
  tile: FlowSlotTile | null
  viewUrl: string | null
  canDisconnect: boolean
  disconnectLabel: string
  disconnectDisabled: boolean
  onOpen: () => void
  onDisconnect: () => void
  className?: string
  style?: CSSProperties
  cardRef?: (element: HTMLDivElement | null) => void
  dragAttributes?: DraggableAttributes
  dragListeners?: DraggableSyntheticListeners
}) {
  return (
    <div ref={cardRef} className={cn(SLOT_CARD, className)} style={style} {...dragAttributes} {...dragListeners}>
      <button
        type="button"
        className="h-10 w-10 shrink-0 overflow-hidden rounded-sm bg-ui-surface-sunken"
        aria-label={name}
        disabled={!viewUrl}
        onClick={onOpen}
      >
        {tile?.video ? (
          <video src={tile.url} className="h-full w-full object-cover" muted playsInline draggable={false} />
        ) : tile ? (
          <img src={tile.url} alt="" className="h-full w-full object-cover" draggable={false} />
        ) : null}
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ui-text-primary">{name}</p>
        <p className="truncate text-xs text-ui-text-muted">{mediaLabel}</p>
      </div>
      {canDisconnect ? (
        <button
          type="button"
          className="shrink-0 cursor-default rounded-sm p-1 text-ui-text-muted hover:bg-ui-surface-sunken hover:text-ui-text-primary"
          aria-label={disconnectLabel}
          disabled={disconnectDisabled}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={onDisconnect}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}
